import * as ko from 'knockout';
import * as $ from 'jquery';

import { JBoxDropDown } from 'Core/Components/JBoxDropdown/DropDown';

import MenuAreasFlyoutTemplate from 'MenuManager/MenuAreas/Templates/MenuAreasFlyout.html';
import { MenuAreaModel } from 'MenuManager/MenuAreas/Models/MenuAreaModel';

/**
 * Configuration object for the `spsCollapsedFlyout` Knockout binding.
 *
 * The binding is designed to show a flyout dropdown (jBox) when the sidebar is collapsed.
 * It supports two modes:
 *  - "area": open flyout content for a MenuAreaModel
 *  - "rootItem": open flyout content for a root menu item model
 */
interface CollapsedFlyoutBindingConfig {
    /**
     * Owner view-model (MenuAreas instance).
     *
     * Required fields / APIs expected on `owner`:
     *  - _isMobile?: KnockoutObservable<boolean> or function returning boolean
     *  - _flyoutVm: view-model used by flyout template
     *  - _flyoutDropDown?: JBoxDropDown instance (created lazily)
     *  - _flyoutArea: KnockoutObservable<MenuAreaModel | null>
     *  - _flyoutRootItem?: KnockoutObservable<any | null>
     *  - _flyoutHideTimer?: number | null
     *  - SetFlyoutHovered(item): sets "hovered" entity in owner (used for highlight/state)
     *  - GetFlyoutHovered?(): returns hovered entity (optional)
     *  - ClearFlyoutHovered?(item?): clears hovered state (optional)
     *  - ClearFlyoutHoveredForce?(): force clear hovered state (optional)
     *  - IsFlyoutLocked?(): boolean-like (optional) - prevents closing when child dropdown is open
     *
     * NOTE:
     * This binding uses `any` because the owner VM is application-specific.
     */
    owner: any;

    /**
     * Flyout mode:
     *  - 'area' (default): uses `cfg.area`
     *  - 'rootItem': uses `cfg.rootItem`
     */
    mode?: 'area' | 'rootItem';

    /**
     * Area model used when mode === 'area'.
     * This is required for the binding to function in 'area' mode.
     */
    area?: MenuAreaModel;

    /**
     * Root menu item used when mode === 'rootItem'.
     * This is required for the binding to function in 'rootItem' mode.
     */
    rootItem?: any;
}

/**
 * CollapsedSidebarFlyoutExtention
 *
 * WHY THIS EXISTS:
 * When sidebar is collapsed, submenu items are no longer visible inline.
 * This extension shows a flyout (jBox dropdown) on hover so users can access submenus.
 *
 * HOW IT WORKS (high level):
 * 1) KO binding attaches pointer/mouse enter/leave handlers to a menu <li>.
 * 2) On enter (when sidebar collapsed): ensure flyout dropdown exists and open it.
 * 3) On leave: schedule a delayed close (prevents flicker).
 * 4) Document-level handlers keep flyout open when pointer moves to flyout or child dropdowns.
 * 5) Outside click closes flyout, except when click happens inside sidebar/flyout/child dropdowns.
 *
 * IMPORTANT BEHAVIOR:
 * - The flyout is controlled manually (closeOnClick/closeOnMouseleave are disabled in jBox).
 * - Closing is delayed via timer to allow "pointer travel" from menu item into flyout.
 * - Flyout can be "locked" by owner state (Recent/Favorites, Queries dropdown opened, etc.).
 *
 * KNOWN PITFALLS / ASSUMPTIONS:
 * - Document-level handlers are bound using `.off(...).on(...)` with namespaces.
 *   This means the latest initialized owner effectively controls the global behavior.
 *   This is usually fine if there is a single sidebar/menu instance on the page.
 * - The flyout box may overflow viewport vertically; we apply translateY shift to keep it visible.
 */
export class CollapsedSidebarFlyoutExtention {
    static Init() {
        ko.bindingHandlers.spsCollapsedFlyout = {
            init: (element, valueAccessor) => {
                const cfg = ko.unwrap(valueAccessor()) as CollapsedFlyoutBindingConfig;

                const owner = cfg?.owner;
                const mode = cfg?.mode || 'area';
                const area = cfg?.area;
                const rootItem = cfg?.rootItem;

                /**
                 * Guard clauses:
                 * - Without owner VM, there is nothing to control.
                 * - Mode requires its corresponding model.
                 */
                if (!owner) return;
                if (mode === 'area' && !area) return;
                if (mode === 'rootItem' && !rootItem) return;

                const $li = $(element);

                /**
                 * ENTER HANDLER
                 *
                 * WHY:
                 * When sidebar is collapsed (desktop), hovering a menu item should show its flyout.
                 *
                 * WHAT:
                 * - Ensure sidebar is collapsed and not in mobile mode.
                 * - Cancel any scheduled close.
                 * - Ensure jBox dropdown is created.
                 * - Determine anchor element for positioning.
                 * - Set hovered entity in owner (for highlighting/state).
                 * - Open flyout in the requested mode.
                 */
                const onEnter = () => {
                    if (!this.IsSidebarClosed(owner)) return;

                    this.CancelClose(owner);
                    this.EnsureDropDown(owner);

                    // Anchor is used as positioning reference for jBox dropdown.
                    const anchor = $li.find('a.nav-link').get(0) as HTMLElement;

                    if (mode === 'area') {
                        owner.SetFlyoutHovered(area);
                        this.OpenArea(owner, area, anchor);
                    } else {
                        owner.SetFlyoutHovered(rootItem);
                        this.OpenRootItem(owner, rootItem, anchor);
                    }
                };

                /**
                 * LEAVE HANDLER
                 *
                 * WHY:
                 * When pointer leaves the menu item, we typically close the flyout.
                 * But we must NOT close if the pointer moved into the flyout itself
                 * or into "child" dropdowns that rely on the flyout state.
                 *
                 * WHAT:
                 * - Check sidebar state again (avoid closing logic in expanded/mobile mode).
                 * - Inspect `relatedTarget` to see where pointer is going.
                 * - If the related target is inside flyout/children, ignore.
                 * - Otherwise schedule close with a delay (anti-flicker).
                 */
                const onLeave = (ev: any) => {
                    if (!this.IsSidebarClosed(owner)) return;

                    const rel = (ev?.relatedTarget as HTMLElement) || null;
                    if (this.IsRelatedToFlyoutOrChildren(rel)) return;

                    // Close is scheduled for the currently hovered entity (area/rootItem).
                    const hovered = mode === 'area' ? area : rootItem;
                    this.ScheduleClose(owner, hovered);
                };

                /**
                 * Bind handlers to the current menu item element.
                 *
                 * NOTE:
                 * Both pointer and mouse events are used for better cross-browser/device coverage.
                 * Namespacing (`.spsCSFlyout`) allows safe cleanup without affecting other handlers.
                 */
                $li.on('pointerenter.spsCSFlyout mouseenter.spsCSFlyout', onEnter);
                $li.on('pointerleave.spsCSFlyout mouseleave.spsCSFlyout', onLeave);

                /**
                 * DOCUMENT-LEVEL "HOLD OPEN" HANDLERS
                 *
                 * WHY:
                 * Moving pointer from <li> into the flyout causes a moment where <li> receives leave,
                 * which would schedule close. These handlers cancel scheduled close while pointer is
                 * inside flyout or related child dropdowns.
                 *
                 * TARGETS:
                 * - .sps-menu-flyout
                 * - .recent-and-favorites-dropdown
                 * - .queries-dropdown
                 *
                 * NOTE:
                 * `.off(...).on(...)` ensures handlers are attached only once (global), avoiding duplicates.
                 */
                $(document)
                    .off('mouseenter.spsCSFlyoutHold pointerenter.spsCSFlyoutHold')
                    .on(
                        'mouseenter.spsCSFlyoutHold pointerenter.spsCSFlyoutHold',
                        '.sps-menu-flyout, .recent-and-favorites-dropdown, .queries-dropdown',
                        () => this.CancelClose(owner)
                    )
                    .off('mouseleave.spsCSFlyoutHold pointerleave.spsCSFlyoutHold')
                    .on(
                        'mouseleave.spsCSFlyoutHold pointerleave.spsCSFlyoutHold',
                        '.sps-menu-flyout, .recent-and-favorites-dropdown, .queries-dropdown',
                        (e: any) => {
                            /**
                             * If flyout is locked (e.g., child dropdown is opened and must remain),
                             * do not schedule close here.
                             */
                            if (owner.IsFlyoutLocked && owner.IsFlyoutLocked()) return;

                            /**
                             * When leaving flyout area, schedule a close using the current hovered entity.
                             * We use optional chaining because owner API may not exist in all contexts.
                             */
                            this.ScheduleClose(owner, owner.GetFlyoutHovered?.());
                        }
                    );

                /**
                 * OUTSIDE CLICK HANDLER
                 *
                 * WHY:
                 * Users expect the flyout to close when they click somewhere outside
                 * of the sidebar/flyout/child dropdowns.
                 *
                 * WHAT:
                 * - Only active in collapsed sidebar desktop mode.
                 * - If locked, ignore outside clicks.
                 * - Ignore clicks within:
                 *    - sidebar menu
                 *    - flyout
                 *    - child dropdown containers
                 * - Otherwise close immediately.
                 */
                $(document)
                    .off('mousedown.spsCSFlyoutOutside')
                    .on('mousedown.spsCSFlyoutOutside', (e: any) => {
                        if (!this.IsSidebarClosed(owner)) return;
                        if (owner.IsFlyoutLocked && owner.IsFlyoutLocked()) return;

                        const $t = $(e.target);

                        if ($t.closest('.page-sidebar-menu').length) return;
                        if ($t.closest('.sps-menu-flyout').length) return;
                        if ($t.closest('.recent-and-favorites-dropdown, .queries-dropdown').length)
                            return;

                        this.Close(owner);
                    });

                /**
                 * KO DISPOSAL CLEANUP
                 *
                 * WHY:
                 * When menu nodes are re-rendered, KO disposes DOM nodes.
                 * We must remove element-level handlers to avoid leaks and ghost behavior.
                 *
                 * NOTE:
                 * Document handlers are intentionally left because they are global and re-bound once.
                 */
                ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
                    $li.off('.spsCSFlyout');
                    // Document handlers are kept (namespaced and bound globally once).
                });
            },
        };
    }

    /**
     * Determines whether sidebar is collapsed and desktop mode is active.
     *
     * WHY:
     * Hover flyout UX is intended only for collapsed sidebar on desktop.
     * On mobile layouts, hover doesn't apply and interactions differ.
     */
    private static IsSidebarClosed(owner: any) {
        const isMobile = owner?._isMobile ? owner._isMobile() : false;
        return $('body').hasClass('page-sidebar-closed') && !isMobile;
    }

    /**
     * Ensures the jBox dropdown instance exists.
     *
     * WHY:
     * Flyout dropdown is created lazily to avoid unnecessary DOM and binding work.
     *
     * DEPENDENCIES:
     * Requires `owner._flyoutVm` to be present. If it is missing, flyout cannot be created.
     */
    private static EnsureDropDown(owner: any) {
        if (owner._flyoutDropDown) return;
        if (!owner._flyoutVm) return;

        owner._flyoutDropDown = new JBoxDropDown({
            target: document.body as any,
            bindComponent: owner._flyoutVm,
            bindOnCreate: true,
            otherOptions: {
                content: MenuAreasFlyoutTemplate as any,

                /**
                 * We disable jBox auto-close triggers because the flyout lifecycle
                 * is handled by our hover/timer/outside-click logic.
                 */
                closeOnMouseleave: false,
                closeOnClick: false,

                position: { x: 'right', y: 'top' },
                outside: 'x',
                pointer: false,

                /**
                 * Fine-tuning alignment near sidebar edge.
                 * A small negative X offset helps avoid visible gap.
                 */
                offset: { x: -1, y: 0 },

                zIndex: 20000,
                addClass: 'sps-menu-flyout',

                /**
                 * Trigger is effectively irrelevant because we call Open() manually.
                 * Still provided to satisfy component options.
                 */
                trigger: 'click',
                maxHeight: 500,
            },
        });
    }

    /**
     * Opens flyout for a MenuAreaModel.
     *
     * WHAT:
     * - Reset any previous viewport shift.
     * - Switch owner VM mode by setting rootItem to null and area to the desired value.
     * - Open jBox at the anchor element.
     * - After render/positioning, shift flyout into viewport if needed.
     */
    private static OpenArea(owner: any, area: MenuAreaModel, anchorEl: HTMLElement) {
        if (!owner._flyoutDropDown) return;

        this.ResetFlyoutShift();

        // Switch flyout VM mode
        owner._flyoutRootItem?.(null);
        owner._flyoutArea(area);

        owner._flyoutDropDown.Open($(anchorEl));

        /**
         * jBox positions the element asynchronously; shifting must happen after it is in DOM.
         * setTimeout(0) defers execution to the next macrotask.
         */
        setTimeout(() => {
            const $box = $('.sps-menu-flyout').last();
            this.ShiftFlyoutIntoViewport($box);
        }, 0);
    }

    /**
     * Opens flyout for a root menu item.
     *
     * WHAT:
     * - Reset any previous viewport shift.
     * - Switch owner VM mode by setting area to null and rootItem to the desired value.
     * - Open jBox at the anchor element.
     * - After render/positioning, shift flyout into viewport if needed.
     */
    private static OpenRootItem(owner: any, rootItem: any, anchorEl: HTMLElement) {
        if (!owner._flyoutDropDown) return;

        this.ResetFlyoutShift();

        // Switch flyout VM mode
        owner._flyoutArea?.(null);
        owner._flyoutRootItem(rootItem);

        owner._flyoutDropDown.Open($(anchorEl));

        // Shift after positioning
        setTimeout(() => {
            const $box = $('.sps-menu-flyout').last();
            this.ShiftFlyoutIntoViewport($box);
        }, 0);
    }

    /**
     * Immediately closes flyout and resets owner state.
     *
     * WHY:
     * Used for outside click and "hard close" scenarios where delay is not needed.
     */
    private static Close(owner: any) {
        this.CancelClose(owner);

        this.ResetFlyoutShift();

        owner._flyoutArea(null);
        owner._flyoutRootItem?.(null);

        // Owner may apply forced hover state; clear it if supported.
        owner.ClearFlyoutHoveredForce?.();

        owner._flyoutDropDown?.Close();
    }

    /**
     * Cancels pending close timer.
     *
     * WHY:
     * Prevents delayed close from firing after we re-enter flyout or open it again.
     */
    private static CancelClose(owner: any) {
        if (owner._flyoutHideTimer) {
            window.clearTimeout(owner._flyoutHideTimer);
            owner._flyoutHideTimer = null;
        }
    }

    /**
     * Schedules delayed flyout closing.
     *
     * WHY:
     * Delay prevents flicker when cursor moves quickly between menu item and flyout.
     *
     * IMPORTANT:
     * If flyout is locked, no timer is scheduled.
     * Lock is checked again inside the timer callback to avoid closing during locked state.
     */
    private static ScheduleClose(owner: any, hoveredItem?: any) {
        this.CancelClose(owner);

        if (owner.IsFlyoutLocked && owner.IsFlyoutLocked()) return;

        owner._flyoutHideTimer = window.setTimeout(() => {
            if (owner.IsFlyoutLocked && owner.IsFlyoutLocked()) return;

            this.ResetFlyoutShift();

            owner._flyoutArea(null);
            owner._flyoutRootItem?.(null);

            // Clears hovered state if owner provides this API.
            owner.ClearFlyoutHovered?.(hoveredItem);

            owner._flyoutDropDown?.Close();
        }, 220);
    }

    /**
     * Determines whether the "related target" is inside flyout or child dropdowns.
     *
     * WHY:
     * Used to avoid scheduling close when pointer leaves the menu item but enters the flyout.
     */
    private static IsRelatedToFlyoutOrChildren(rel: HTMLElement | null) {
        if (!rel) return false;
        const $r = $(rel);
        return (
            $r.closest('.sps-menu-flyout, .recent-and-favorites-dropdown, .queries-dropdown')
                .length > 0
        );
    }

    /**
     * Resets viewport shift state for flyout box.
     *
     * WHY:
     * We may apply `transform: translateY(...)` to keep flyout within viewport.
     * Before opening or re-computing shift, we must remove previous shift.
     *
     * NOTE:
     * Dataset flag `spsHadTransform` is used as a marker for debugging/traceability.
     */
    private static ResetFlyoutShift($box?: JQuery) {
        const $b = $box && $box.length ? $box : $('.sps-menu-flyout').last();
        if (!$b.length) return;

        // The transform is applied on the flyout wrapper element.
        const el = $b.get(0) as HTMLElement;

        /**
         * If we ever need to preserve a pre-existing transform, this marker can be used.
         * Currently, the code clears transform unconditionally (as originally implemented).
         */
        const hadTransform = el.dataset.spsHadTransform === '1';
        if (!hadTransform) {
            el.style.transform = '';
        } else {
            el.style.transform = '';
        }

        delete el.dataset.spsHadTransform;
        $b.removeClass('sps-menu-flyout--shifted');
    }

    /**
     * Applies a vertical shift so the flyout fits inside the viewport.
     *
     * WHY:
     * Flyout height can exceed available viewport area near top/bottom edges.
     * Shifting improves usability by keeping the content fully accessible.
     *
     * WHAT:
     * - Reset prior shift.
     * - Compute overflow (top/bottom) using bounding rect.
     * - Apply translateY only (do not modify X positioning from jBox).
     */
    private static ShiftFlyoutIntoViewport($box: JQuery) {
        if (!$box?.length) return;

        const el = $box.get(0) as HTMLElement;

        // Reset previous shift for correct rect calculations
        this.ResetFlyoutShift($box);

        // Save whether element had a transform before shifting (debug/trace marker)
        if (el.dataset.spsHadTransform == null) {
            const t = getComputedStyle(el).transform;
            el.dataset.spsHadTransform = t && t !== 'none' ? '1' : '0';
        }

        const margin = 8;
        const viewportH = window.innerHeight;

        const rect = el.getBoundingClientRect();

        /**
         * overflowBottom > 0 means flyout extends below viewport bottom margin.
         * overflowTop > 0 means flyout extends above viewport top margin.
         */
        const overflowBottom = rect.bottom - (viewportH - margin);
        const overflowTop = margin - rect.top;

        // If there is no overflow, no shift is needed.
        if (overflowBottom <= 0 && overflowTop <= 0) return;

        // Mark as shifted for styling/debugging.
        $box.addClass('sps-menu-flyout--shifted');

        let deltaY = 0;

        // Shift upwards to fix bottom overflow.
        if (overflowBottom > 0) deltaY -= overflowBottom;

        // Ensure we do not move above the top margin.
        const predictedTop = rect.top + deltaY;
        if (predictedTop < margin) deltaY += margin - predictedTop;

        /**
         * Apply only translateY to avoid breaking horizontal alignment managed by jBox.
         * We round delta to whole pixels for stable rendering.
         */
        el.style.transform = `translateY(${Math.round(deltaY)}px)`;
    }
}
